/**
 * Stack-first homepage hero. One job: get a ZIP and run the Stack.
 *
 * NOTE: The `data-hero-*` attributes below are coupled to the inlined critical
 * CSS in `src/routes/__root.tsx`. If you rename or remove them, update that
 * inline <style> block to match — otherwise first paint will regress.
 */
import { useEffect } from "react";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { Link } from "@/lib/router-compat";
import StackZipForm from "./StackZipForm";
import { NO_COMMISSION_LINE } from "@/data/disclosuresCopy";

import heroPorchWebp from "@/assets/home/hero-porch.webp.asset.json";
import heroPorchJpg from "@/assets/home/hero-porch.jpg.asset.json";

// The A/B/C headline test is retired: the Stack-first hero ships to 100% of
// traffic as variant "S" so the dataLayer timeline stays continuous.
const HERO_VARIANT = "S";

const HonestHero = () => {
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem("hero_variant", HERO_VARIANT);
    } catch {
      /* storage unavailable — analytics still fires below */
    }
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({ event: "hero_variant_view", variant: HERO_VARIANT });
  }, []);

  return (
    <section data-hero-section className="relative isolate overflow-hidden bg-primary pt-12 pb-12 md:pt-20 md:pb-16">
      {/*
        Backdrop photo + navy scrim. Both are positioned by the inlined
        critical CSS (data-hero-bg / data-hero-scrim) so they paint with the
        first byte rather than waiting on the Tailwind bundle. The image is
        preloaded in src/routes/__root.tsx — keep the two hrefs in sync.
      */}
      <picture>
        <source srcSet={heroPorchWebp.url} type="image/webp" />
        <img
          data-hero-bg
          src={heroPorchJpg.url}
          alt=""
          aria-hidden
          fetchPriority="high"
          decoding="async"
        />
      </picture>
      <div data-hero-scrim aria-hidden />
      <div className="container mx-auto max-w-4xl px-4 flex flex-col items-center text-center">

        <p
          data-hero-proof
          className="flex items-center gap-2 text-sm text-primary-foreground/85 mb-5"
        >
          <ShieldCheck className="h-4 w-4 text-accent shrink-0" aria-hidden />
          <span>A homeownership benefit offered through employers</span>
        </p>

        <h1
          data-hero-h1
          className="text-4xl md:text-5xl lg:text-6xl text-primary-foreground font-normal leading-[1.12] mb-7 max-w-3xl text-balance"
          style={{ fontFamily: "var(--heading-font, serif)" }}
        >
          Find every dollar available for your{" "}
          <em className="italic text-accent">down payment</em>
        </h1>

        {/* Primary CTA: Homie's Readiness Score. Stack sits directly under it. */}
        <Link
          to="/request-access"
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-accent px-8 h-14 text-base font-semibold text-accent-foreground hover:bg-accent/90 transition-colors"
        >
          Request access through my employer
          <ArrowRight className="h-5 w-5" aria-hidden />
        </Link>
        <p className="mt-3 text-sm text-primary-foreground/75 max-w-md">
          Home Approach is available only through employers. Tell us where you work and we'll reach out.{" "}
          <Link to="/auth?signin=true" className="underline font-semibold text-primary-foreground">Already a member? Sign in</Link>
        </p>
        <p className="mt-3 flex items-center gap-2 text-sm font-semibold text-primary-foreground">
          <ShieldCheck className="h-4 w-4 text-accent shrink-0" aria-hidden />
          <span>{NO_COMMISSION_LINE}. Not from agents, not from lenders, not when you close.</span>
        </p>

        <div className="mt-8 w-full flex flex-col items-center">
          <p className="mb-3 text-xs uppercase tracking-wide text-primary-foreground/60">
            Curious? Preview your Stack
          </p>
          <StackZipForm section="hero" ctaAttr />
        </div>

        <p className="mt-5 text-sm text-primary-foreground/75 max-w-md">
          The Home Approach Stack combines federal, state, county, and employer
          aid into one total. Full details unlock through your employer.
        </p>

      </div>
    </section>
  );
};

export default HonestHero;
